// usePopoverDismiss — shared dismissal for header popovers (Wallet, Profile, …).
// While `open`, closes on a mousedown outside `containerRef` and on Escape; on
// Escape it also returns focus to `triggerRef` (so keyboard users keep their
// place). Listeners are installed only while open and removed on close/unmount.
//
// onClose is read through a ref so a fresh inline callback each render does not
// re-subscribe the listeners — they re-bind only when `open` flips.

import { type RefObject, useEffect, useRef } from 'react'

export function usePopoverDismiss(
  containerRef: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
  triggerRef?: RefObject<HTMLElement | null>
) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCloseRef.current()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
        triggerRef?.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, containerRef, triggerRef])
}
